"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { looksLikeCharacterCodes } from "@/lib/tool-kit/developer/encoding-detect";

type Direction = "auto" | "to-codes" | "to-text";

const CODE_SEPARATOR_REGEX = /[\s,]+/;
const MAX_CODE_POINT = 0x10_ff_ff;

type Strings = { [K in keyof typeof ASCII_CONVERTER_STRINGS]: string };

const DIRECTIONS: { value: Direction; label: keyof Strings }[] = [
  { value: "auto", label: "directionAuto" },
  { value: "to-codes", label: "directionToCodes" },
  { value: "to-text", label: "directionToText" },
];

const CONTROL_CHARS: {
  code: number;
  char: string;
  description: keyof Strings;
}[] = [
  { code: 9, char: "TAB", description: "controlTab" },
  { code: 10, char: "LF", description: "controlLineFeed" },
  { code: 13, char: "CR", description: "controlCarriageReturn" },
  { code: 32, char: "SP", description: "controlSpace" },
];

const PRINTABLE_CODES: number[] = [];
for (let code = 33; code <= 126; code++) {
  PRINTABLE_CODES.push(code);
}

function encodeToCodes(text: string): {
  output: string;
  count: number;
  error: string;
} {
  const codes: string[] = [];
  for (const char of text) {
    codes.push(String(char.codePointAt(0)));
  }
  return { output: codes.join(" "), count: codes.length, error: "" };
}

function decodeFromCodes(
  input: string,
  strings: Strings
): {
  output: string;
  count: number;
  error: string;
} {
  const parts = input.trim().split(CODE_SEPARATOR_REGEX);
  const values: number[] = [];
  for (const part of parts) {
    const value = Number.parseInt(part, 10);
    if (Number.isNaN(value) || String(value) !== part) {
      return {
        output: "",
        count: 0,
        error: t(strings.errorNotWholeNumber, { part }),
      };
    }
    if (value > MAX_CODE_POINT) {
      return {
        output: "",
        count: 0,
        error: t(strings.errorAboveMaximum, { value: String(value) }),
      };
    }
    values.push(value);
  }
  return {
    output: String.fromCodePoint(...values),
    count: values.length,
    error: "",
  };
}

function convert(
  input: string,
  direction: Direction,
  strings: Strings
): { output: string; count: number; error: string; decoded: boolean } {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { output: "", count: 0, error: "", decoded: false };
  }
  const shouldDecode =
    direction === "to-text" ||
    (direction === "auto" && looksLikeCharacterCodes(trimmed));
  if (shouldDecode) {
    return { ...decodeFromCodes(trimmed, strings), decoded: true };
  }
  return { ...encodeToCodes(input), decoded: false };
}

/** English strings. A translated content file overrides these through `ui`. */
export const ASCII_CONVERTER_STRINGS = {
  direction: "Direction",
  textOrCodes: "Text or codes",
  hiOr72105: "Hi  —or—  72 105",
  decodedText: "Decoded text",
  characterCodes: "Character codes",
  character: "one: character|other: characters",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  asciiReferenceTable: "ASCII Reference Table",
  commonControlCodesFollowedBy:
    "Common control codes followed by every printable ASCII character (codes 33–126).",
  code: "Code",
  character2: "Character",
  description: "Description",
  printableCharacter: "Printable character",
  directionAuto: "Auto-detect",
  directionToCodes: "Text → Codes",
  directionToText: "Codes → Text",
  errorNotWholeNumber:
    '"{part}" is not a whole number. Separate codes with spaces or commas.',
  errorAboveMaximum:
    "{value} is above the maximum Unicode code point (1114111).",
  controlTab: "Horizontal tab",
  controlLineFeed: "Line feed (new line)",
  controlCarriageReturn: "Carriage return",
  controlSpace: "Space",
} as const;

export default function AsciiConverterWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("auto");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(ASCII_CONVERTER_STRINGS);
  const { output, count, error, decoded } = convert(input, direction, strings);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const fmt = useToolNumber();

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {strings[d.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ascii-input"
        >
          {strings.textOrCodes}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ascii-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.hiOr72105}
          value={input}
        />
      </div>
      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ascii-output"
          >
            {decoded ? strings.decodedText : strings.characterCodes}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="ascii-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {fmt(count)}
            {tPlural(strings.character, locale, count)}
          </p>
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* ASCII reference table */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.asciiReferenceTable}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.commonControlCodesFollowedBy}
        </p>
        <div className="max-h-[320px] overflow-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="px-4 py-2 font-medium text-foreground">
                  {strings.code}
                </th>
                <th className="px-4 py-2 font-medium text-foreground">
                  {strings.character2}
                </th>
                <th className="px-4 py-2 font-medium text-foreground">
                  {strings.description}
                </th>
              </tr>
            </thead>
            <tbody>
              {CONTROL_CHARS.map((row) => (
                <tr className="border-border border-t" key={row.code}>
                  <td className="px-4 py-1.5 font-mono text-muted-foreground">
                    {row.code}
                  </td>
                  <td className="px-4 py-1.5 font-mono text-foreground">
                    {row.char}
                  </td>
                  <td className="px-4 py-1.5 text-muted-foreground">
                    {strings[row.description]}
                  </td>
                </tr>
              ))}
              {PRINTABLE_CODES.map((code) => (
                <tr className="border-border border-t" key={code}>
                  <td className="px-4 py-1.5 font-mono text-muted-foreground">
                    {code}
                  </td>
                  <td className="px-4 py-1.5 font-mono text-foreground">
                    {String.fromCharCode(code)}
                  </td>
                  <td className="px-4 py-1.5 text-muted-foreground">
                    {strings.printableCharacter}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
